import React from 'react';
import Helmet from 'react-helmet';
import Waypoint from 'react-waypoint';
import debounce from 'lodash/debounce';

import Snoozes from 'api/resources/snoozes';
import getErrors from 'utils/getErrors';
import { Deserializer } from 'jsonapi-serializer';

import { ReactComponent as SnoozesIcon } from 'icons/Snoozes.svg';

import Loader from 'components/Loader';
import SearchForm from 'components/SearchForm';
import SnoozesInactiveItem from 'admin-editor/containers/SnoozesInactiveItem';
import ActiveSnoozes from 'admin-editor/containers/ActiveSnoozes';
import CreateSnooze from 'admin-editor/containers/CreateSnooze';

import ErrorText from 'styled/Page/ErrorText';
import CenteredContainer from 'styled/Page/CenteredContainer';
import RightColumn from 'styled/Page/RightColumn';
import Main from 'styled/Page/Main';
import HeaderTitle from 'styled/Page/HeaderTitle';
import Header from 'styled/Page/Header';
import SimpleList from 'styled/SimpleList';

const PAGE_SIZE = 15;

export class SnoozesPage extends React.PureComponent {
  state = {
    hasMore: false,
    loading: false,
    reloadOnActivateSnooze: false,
    snoozes: [],
    errors: [],
    searchValue: undefined,
  }

  loadSnoozesWithDebounce = debounce(() => {
    window.scrollTo(0, 0);
    this.setState({ snoozes: [] }, this.loadSnoozes);
  }, 500);

  componentDidMount() {
    this.loadSnoozes();
  }

  get cursor() {
    if (this.state.snoozes.length === 0) return undefined;
    return this.state.snoozes[this.state.snoozes.length - 1].id;
  }

  handleSnoozesUpdate = ({ id, reloadOnActivateSnooze }) => {
    this.setState(prevState => ({
      snoozes: prevState.snoozes.filter(item => item.id !== id),
      reloadOnActivateSnooze: reloadOnActivateSnooze ? !prevState.reloadOnActivateSnooze : prevState.reloadOnActivateSnooze,
    }));
  }

  handleCreate = (snooze) => {
    this.setState(prevState => ({
      snoozes: [snooze].concat(prevState.snoozes),
    }));
  }

  handleDeactivate = (snooze) => {
    this.setState(prevState => ({
      snoozes: [snooze].concat(prevState.snoozes),
    }));
  }

  handleSearchChange = (e) => {
    this.setState({ searchValue: e.target.value });
    this.loadSnoozesWithDebounce();
  }

  handleSearchSubmit = (e) => {
    e.preventDefault();

    this.setState({ snoozes: [] }, this.loadSnoozes);
  }

  handleSearchClose = () => {
    this.setState({
      searchValue: undefined,
      snoozes: [],
    }, this.loadSnoozes);
  }

  loadSnoozes = async () => {
    this.setState({
      loading: true,
      errors: [],
    });

    try {
      const response = await Snoozes.getAll({
        page: {
          cursor: this.cursor,
          size: PAGE_SIZE,
        },
        filter: {
          q: this.state.searchValue,
          is_active: false,
        },
        fields: { snooze: 'name' },
      });

      const snoozes = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);

      this.setState(prevState => ({
        snoozes: snoozes.reduce((acc, currentValue) => {
          if (acc.find(item => item.id === currentValue.id)) {
            return acc;
          }

          return acc.concat(currentValue);
        }, prevState.snoozes),
        loading: false,
        hasMore: snoozes.length === PAGE_SIZE,
      }));
    } catch (err) {
      getErrors(err).then((errors) => {
        this.setState({
          errors,
          loading: false,
        });
      });
    }
  }

  render() {
    return (
      <Main>
        <Helmet><title>Снузы</title></Helmet>

        <div style={{ width: '100%', paddingTop: '14px' }}>
          <Header>
            <HeaderTitle style={{ marginRight: 'auto' }}>снузы</HeaderTitle>

            <SearchForm
              onChange={this.handleSearchChange}
              onSubmit={this.handleSearchSubmit}
              onClose={this.handleSearchClose}
              value={this.state.searchValue}
            />
          </Header>

          {this.state.snoozes.length !== 0 &&
            this.state.errors.length === 0 && (
            <SimpleList style={{ padding: '8px 32px 7px 24px' }}>
              {this.state.snoozes.map(item => (
                <SnoozesInactiveItem
                  key={item.id}
                  id={item.id}
                  name={item.name}
                  onSnoozesUpdate={this.handleSnoozesUpdate}
                />
              ))}
            </SimpleList>
          )}

          {this.state.loading && <CenteredContainer><Loader /></CenteredContainer>}

          {this.state.errors.length > 0 && (
            <CenteredContainer>
              {this.state.errors.map(error => (
                <ErrorText role="alert" key={error}>{error}</ErrorText>
              ))}
            </CenteredContainer>
          )}

          {!this.state.loading &&
            this.state.snoozes.length === 0 &&
            this.state.errors.length === 0 && (
            <CenteredContainer>
              <SnoozesIcon />

              <ErrorText>Cнузы не найдены</ErrorText>
            </CenteredContainer>
          )}

          {this.state.hasMore && <Waypoint onEnter={this.loadSnoozes} />}
        </div>

        <RightColumn style={{ padding: '14px 0 0' }}>
          <ActiveSnoozes
            reloadOnActivateSnooze={this.state.reloadOnActivateSnooze}
            onDeactivate={this.handleDeactivate}
          />
        </RightColumn>

        <RightColumn>
          <CreateSnooze onCreate={this.handleCreate} />
        </RightColumn>
      </Main>
    );
  }
}

export default SnoozesPage;
